import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Container,
  Dropdown,
  Form,
  Header,
  Icon,
  Input,
  Message,
  Modal,
  Segment,
  Table
} from 'semantic-ui-react';
import { useTheme } from './ThemeContext';
import { useToast } from './utils/Toast';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { isMockAuthEnabled, clearMockSession, getMockUsers } from './utils/mockAuth';
import { handleSessionExpired } from './utils/sessionHandler';
import { useHealthCheck } from './hooks/useHealthCheck';
import { useApiCache, CacheKeys } from './hooks/useApiCache';
import {
  ClientUserSummary,
  fetchManagedClients,
  inviteClientAccount,
  updateClientUser,
  deleteClientAccount
} from './api/users';
import { AuthTokenExpiredError, AccessDeniedError } from './api/sites';
import './SiteDashboard.css';
import './AdminDashboard.css';

interface CreateUserFormState {
  email: string;
  fullName: string;
  role: string;
}

interface EditUserFormState {
  email: string;
  fullName: string;
  role: string;
}

const AdminDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { role, refreshSession, loading: authLoading } = useAuth();
  // Health check is handled by useHealthCheck hook globally
  useHealthCheck();
  const { getOrFetch, invalidatePattern } = useApiCache();

  const [users, setUsers] = useState<ClientUserSummary[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Create user modal
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [createForm, setCreateForm] = useState<CreateUserFormState>({ email: '', fullName: '', role: 'client' });
  const [createSubmitting, setCreateSubmitting] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Edit user modal
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<ClientUserSummary | null>(null);
  const [editForm, setEditForm] = useState<EditUserFormState>({ email: '', fullName: '', role: 'client' });
  const [editSubmitting, setEditSubmitting] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteUser, setDeleteUser] = useState<ClientUserSummary | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState<boolean>(false);

  // Load all users
  const loadUsers = useCallback(async () => {
    if (role !== 'admin') {
      setUsers([]);
      return;
    }

    try {
      setUsersLoading(true);
      
      // Return mock data if mock auth is enabled
      if (isMockAuthEnabled()) {
        const mockUsers = getMockUsers() as any;
        const sortedUsers = mockUsers.sort((a: any, b: any) => {
          const nameA = (a.fullName || a.email).toLowerCase();
          const nameB = (b.fullName || b.email).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setUsers(sortedUsers);
        setUsersLoading(false);
        return;
      }
      
      // Use cache to avoid redundant requests
      const response = await getOrFetch(
        CacheKeys.managedClients({ roles: ['client', 'admin_client'], createdBy: null }),
        () => fetchManagedClients({ 
          roles: ['client', 'admin_client'], 
          createdBy: null // Get all users, not just created by current admin
        }),
        { ttl: 3 * 60 * 1000 } // Cache for 3 minutes
      );
      
      // Sort alphabetically by name or email
      const sortedUsers = response.sort((a, b) => {
        const nameA = (a.fullName || a.email).toLowerCase();
        const nameB = (b.fullName || b.email).toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setUsers(sortedUsers);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        handleSessionExpired(showToast);
        return;
      }

      if (error instanceof AccessDeniedError) {
        showToast('Access denied', 'error');
        navigate('/');
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to load users.';
      showToast(message, 'error');
    } finally {
      setUsersLoading(false);
    }
  }, [role, showToast, navigate, getOrFetch]);

  const logoSrc = theme === 'dark' 
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  // Filter users based on search query - memoized to prevent unnecessary re-calculations
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const searchTerm = searchQuery.toLowerCase();
      const name = (user.fullName || '').toLowerCase();
      const email = user.email.toLowerCase();
      return name.includes(searchTerm) || email.includes(searchTerm);
    });
  }, [users, searchQuery]);

  // Memoized role options to prevent unnecessary re-creation
  const roleOptions = useMemo(() => [
    { key: 'client', text: 'User', value: 'client' },
    { key: 'admin_client', text: 'Manager', value: 'admin_client' },
    { key: 'admin', text: 'Admin', value: 'admin' }
  ], []);

  // Helper function to get role display text
  const getRoleDisplayText = (role: string | null | undefined): string => {
    switch (role) {
      case 'client':
        return 'User';
      case 'admin_client':
        return 'Manager';
      case 'admin':
        return 'Admin';
      default:
        return 'User';
    }
  };

  // Create new user
  const handleCreateUser = async () => {
    const trimmedEmail = createForm.email.trim();
    const trimmedFullName = createForm.fullName.trim();

    if (!trimmedEmail) {
      setCreateError('Email is required.');
      return;
    }

    if (!trimmedFullName) {
      setCreateError('Full name is required.');
      return;
    }

    setCreateSubmitting(true);
    setCreateError(null);

    try {
      const userSummary = await inviteClientAccount({
        email: trimmedEmail.toLowerCase(),
        fullName: trimmedFullName,
        role: createForm.role
      });

      // Invalidate user cache after successful creation
      invalidatePattern('users:');

      setUsers(prev => [userSummary, ...prev].sort((a, b) => {
        const nameA = (a.fullName || a.email).toLowerCase();
        const nameB = (b.fullName || b.email).toLowerCase();
        return nameA.localeCompare(nameB);
      }));

      showToast(`User ${trimmedFullName} created successfully`, 'success');
      setCreateForm({ email: '', fullName: '', role: 'client' });
      setCreateModalOpen(false);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        showToast(error.message, 'warning');
        void refreshSession();
        navigate('/');
        return;
      }

      if (error instanceof AccessDeniedError) {
        showToast('Access denied', 'error');
        return;
      }

      const message = error instanceof Error ? error.message : 'Unable to create user.';
      setCreateError(message);
    } finally {
      setCreateSubmitting(false);
    }
  };

  // Open edit modal
  const handleEditUser = (user: ClientUserSummary) => {
    setEditUser(user);
    setEditForm({
      email: user.email,
      fullName: user.fullName || '',
      role: user.role || 'client'
    });
    setEditError(null);
    setEditModalOpen(true);
  };

  // Open delete confirmation
  const handleDeleteUser = (user: ClientUserSummary) => {
    setDeleteUser(user);
    setDeleteModalOpen(true);
  };

  // Confirm delete user
  const confirmDeleteUser = async () => {
    if (!deleteUser) return;

    setDeleteSubmitting(true);

    try {
      await deleteClientAccount(deleteUser.id);
      
      // Invalidate user cache after successful deletion
      invalidatePattern('users:');
      
      setUsers(prev => prev.filter(u => u.id !== deleteUser.id));
      showToast(`User ${deleteUser.fullName || deleteUser.email} deleted successfully`, 'success');
      setDeleteModalOpen(false);
      setDeleteUser(null);
    } catch (error) {
      if (error instanceof AuthTokenExpiredError) {
        showToast(error.message, 'warning');
        void refreshSession();
        navigate('/');
        return;
      }

      // Admins should be able to delete any user, so no access denied check
      const message = error instanceof Error ? error.message : 'Unable to delete user.';
      showToast(message, 'error');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (isMockAuthEnabled()) {
        clearMockSession();
        await refreshSession();
        showToast('Logged out successfully', 'success');
        navigate('/');
        return;
      }
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        throw error;
      }
      await refreshSession();
      showToast('Logged out successfully', 'success');
      navigate('/');
    } catch (err) {
      console.error('[AdminDashboard] Failed to sign out', err);
      showToast('Unable to log out right now. Please try again.', 'error');
    }
  };

  if (authLoading || usersLoading) {
    return (
      <div className="dashboard-container">
        <Container>
          <Message>Loading...</Message>
        </Container>
      </div>
    );
  }

  if (role !== 'admin') {
    return (
      <div className="dashboard-container">
        <Container>
          <Message negative>
            <Message.Header>Access Denied</Message.Header>
            <p>You don't have permission to access the admin dashboard.</p>
          </Message>
        </Container>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <Container>
          <div className="header-content">
            <div className="header-logo">
              <img 
                src={logoSrc} 
                alt="Pilbara Risk Solutions" 
                className="dashboard-logo" 
              />
            </div>
            <div className="header-actions">
              <Button 
                icon="building" 
                content="Site Dashboard" 
                onClick={() => navigate('/site-dashboard')}
                className="header-button"
              />
              <Button 
                icon="sign-out" 
                content="Logout" 
                onClick={handleLogout}
                className="logout-button"
              />
            </div>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <Container>
          <Header as="h1" className="dashboard-title">
            <Icon name="users" />
            Admin Dashboard
          </Header>

          {/* Controls */}
          <Segment className="dashboard-controls">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                <Input
                  icon="search"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ minWidth: '300px' }}
                />
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                  {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
                </span>
              </div>
              <Button
                primary
                icon="user plus"
                content="Create New User"
                onClick={() => setCreateModalOpen(true)}
              />
            </div>
          </Segment>

          {/* Users Table */}
          <Segment>
            {filteredUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                <Icon name="users" size="large" />
                <p>{searchQuery ? 'No users match your search.' : 'No users found.'}</p>
              </div>
            ) : (
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Name</Table.HeaderCell>
                    <Table.HeaderCell>Email</Table.HeaderCell>
                    <Table.HeaderCell>Role</Table.HeaderCell>
                    <Table.HeaderCell>Created</Table.HeaderCell>
                    <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {filteredUsers.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Icon name="user circle" />
                        {user.fullName || 'No name'}
                      </Table.Cell>
                      <Table.Cell>{user.email}</Table.Cell>
                      <Table.Cell>
                        <span style={{ 
                          padding: '0.3rem 0.6rem', 
                          borderRadius: '4px', 
                          backgroundColor: user.role === 'admin_client' ? '#e3f2fd' : user.role === 'admin' ? '#fff3e0' : '#f3e5f5',
                          color: user.role === 'admin_client' ? '#1976d2' : user.role === 'admin' ? '#e65100' : '#7b1fa2',
                          fontSize: '0.8rem',
                          fontWeight: 'bold'
                        }}>
                          {getRoleDisplayText(user.role)}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                      </Table.Cell>
                      <Table.Cell>
                        <Button.Group size="small">
                          <Button
                            icon="cog"
                            onClick={() => handleEditUser(user)}
                            title="Edit user"
                          />
                          <Button
                            icon="trash"
                            color='red'
                            onClick={() => handleDeleteUser(user)}
                            title="Delete user"
                          />
                        </Button.Group>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Segment>
        </Container>
      </div>

      {/* Create User Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => {
          if (!createSubmitting) {
            setCreateModalOpen(false);
            setCreateError(null);
            setCreateForm({ email: '', fullName: '', role: 'client' });
          }
        }}
        size="small"
      >
        <Modal.Header>
          <Icon name="user plus" />
          Create New User
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label="Full Name"
              placeholder="Enter full name"
              required
              value={createForm.fullName}
              onChange={(e) => setCreateForm(prev => ({ ...prev, fullName: e.target.value }))}
            />
            <Form.Input
              label="Email"
              placeholder="user@example.com"
              type="email"
              required
              value={createForm.email}
              onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <Form.Field>
              <label>Role</label>
              <Dropdown
                placeholder="Select role"
                fluid
                selection
                value={createForm.role}
                onChange={(e, { value }) => setCreateForm(prev => ({ ...prev, role: value as string }))}
                options={roleOptions}
              />
            </Form.Field>
          </Form>
          {createError && (
            <Message negative>
              <Message.Header>Unable to create user</Message.Header>
              <p>{createError}</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              if (!createSubmitting) {
                setCreateModalOpen(false);
                setCreateError(null);
                setCreateForm({ email: '', fullName: '', role: 'client' });
              }
            }}
            disabled={createSubmitting}
          >
            Cancel
          </Button>
          <Button
            primary
            loading={createSubmitting}
            disabled={createSubmitting || !createForm.email.trim() || !createForm.fullName.trim()}
            onClick={handleCreateUser}
          >
            Create User
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditError(null);
          setEditUser(null);
        }}
        size="small"
      >
        <Modal.Header>
          <Icon name="cog" />
          Edit User: {editUser?.fullName || editUser?.email}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label="Full Name"
              placeholder="Enter full name"
              value={editForm.fullName}
              onChange={(e) => setEditForm(prev => ({ ...prev, fullName: e.target.value }))}
            />
            <Form.Input
              label="Email"
              placeholder="user@example.com"
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
            />
            <Form.Field>
              <label>Role</label>
              <Dropdown
                placeholder="Select role"
                fluid
                selection
                value={editForm.role}
                onChange={(e, { value }) => setEditForm(prev => ({ ...prev, role: value as string }))}
                options={roleOptions}
              />
            </Form.Field>
          </Form>
          {editError && (
            <Message negative>
              <Message.Header>Unable to update user</Message.Header>
              <p>{editError}</p>
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              setEditModalOpen(false);
              setEditError(null);
              setEditUser(null);
            }}
          >
            Cancel
          </Button>
          <Button
            primary
            loading={editSubmitting}
            onClick={async () => {
              if (editUser) {
                setEditSubmitting(true);
                try {
                  const updatedUser = await updateClientUser(editUser.id, {
                    fullName: editForm.fullName,
                    role: editForm.role
                  });
                  
                  // Invalidate user cache after successful update
                  invalidatePattern('users:');
                  
                  // Update the users list with the updated user
                  setUsers(prevUsers => 
                    prevUsers.map(user => 
                      user.id === updatedUser.id ? updatedUser : user
                    )
                  );
                  
                  showToast('User updated successfully!', 'success');
                  setEditModalOpen(false);
                } catch (error) {
                  console.error('Failed to update user:', error);
                  showToast('Failed to update user. Please try again.', 'error');
                } finally {
                  setEditSubmitting(false);
                }
              }
            }}
          >
            Save Changes
          </Button>
        </Modal.Actions>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteModalOpen(false);
            setDeleteUser(null);
          }
        }}
        size="small"
      >
        <Modal.Header>
          <Icon name="warning sign" color="red" />
          Confirm Delete
        </Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to delete the user <strong>{deleteUser?.fullName || deleteUser?.email}</strong>?
          </p>
          <p style={{ color: 'var(--text-secondary)' }}>
            This action cannot be undone.
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => {
              if (!deleteSubmitting) {
                setDeleteModalOpen(false);
                setDeleteUser(null);
              }
            }}
            disabled={deleteSubmitting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            loading={deleteSubmitting}
            disabled={deleteSubmitting}
            onClick={confirmDeleteUser}
          >
            <Icon name="trash" />
            Delete User
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default AdminDashboard;